import React from 'react'
import PropTypes from 'prop-types'
import { FileText, Calendar, Target, Building2, Globe, Briefcase, User } from 'lucide-react'

/**
 * Basic Details Form Component
 *
 * Allows editing basic project information:
 * - Name, Description
 * - Type, Priority, Status
 * - Assigned Project Manager
 * - Start Date, Target Date
 * - Industry, Domain
 */
const BasicDetailsForm = ({ data, onChange, disabled, teamMembers = [] }) => {
	const handleChange = (e) => {
		const { name, value } = e.target
		onChange(name, value)
	}

	const projectTypes = [
		{ value: 'compliance', label: 'Compliance' },
		{ value: 'audit', label: 'Audit' },
		{ value: 'certification', label: 'Certification' },
		{ value: 'assessment', label: 'Assessment' },
	]

	const priorities = [
		{ value: 'low', label: 'Low' },
		{ value: 'medium', label: 'Medium' },
		{ value: 'high', label: 'High' },
		{ value: 'critical', label: 'Critical' },
	]

	const statuses = [
		{ value: 'planning', label: 'Planning' },
		{ value: 'in_progress', label: 'In Progress' },
		{ value: 'review', label: 'Under Review' },
		{ value: 'on_hold', label: 'On Hold' },
		{ value: 'completed', label: 'Completed' },
		{ value: 'cancelled', label: 'Cancelled' },
	]

	const industries = [
		{ value: '', label: 'Select Industry' },
		{ value: 'technology', label: 'Technology' },
		{ value: 'healthcare', label: 'Healthcare' },
		{ value: 'finance', label: 'Finance & Banking' },
		{ value: 'retail', label: 'Retail & E-commerce' },
		{ value: 'manufacturing', label: 'Manufacturing' },
		{ value: 'education', label: 'Education' },
		{ value: 'government', label: 'Government' },
		{ value: 'energy', label: 'Energy & Utilities' },
		{ value: 'other', label: 'Other' },
	]

	return (
		<div className="basic-details-form">
			<div className="form-section">
				<h3 className="form-section__title">
					<FileText size={20} />
					Project Information
				</h3>

				<div className="form-group">
					<label htmlFor="name" className="form-label required">
						Project Name
					</label>
					<input
						type="text"
						id="name"
						name="name"
						className="form-input"
						value={data.name || ''}
						onChange={handleChange}
						disabled={disabled}
						placeholder="Enter project name"
						required
					/>
				</div>

				<div className="form-group">
					<label htmlFor="description" className="form-label">
						Description
					</label>
					<textarea
						id="description"
						name="description"
						className="form-textarea"
						value={data.description || ''}
						onChange={handleChange}
						disabled={disabled}
						placeholder="Describe the project objectives and scope"
						rows={4}
					/>
				</div>

				<div className="form-row form-row--3">
					<div className="form-group">
						<label htmlFor="type" className="form-label">
							<Briefcase size={16} />
							Project Type
						</label>
						<select
							id="type"
							name="type"
							className="form-select"
							value={data.type || 'compliance'}
							onChange={handleChange}
							disabled={disabled}
						>
							{projectTypes.map((type) => (
								<option key={type.value} value={type.value}>
									{type.label}
								</option>
							))}
						</select>
					</div>

					<div className="form-group">
						<label htmlFor="priority" className="form-label">
							<Target size={16} />
							Priority
						</label>
						<select
							id="priority"
							name="priority"
							className="form-select"
							value={data.priority || 'medium'}
							onChange={handleChange}
							disabled={disabled}
						>
							{priorities.map((priority) => (
								<option key={priority.value} value={priority.value}>
									{priority.label}
								</option>
							))}
						</select>
					</div>

					<div className="form-group">
						<label htmlFor="status" className="form-label">
							Status
						</label>
						<select
							id="status"
							name="status"
							className="form-select"
							value={data.status || 'planning'}
							onChange={handleChange}
							disabled={disabled}
						>
							{statuses.map((status) => (
								<option key={status.value} value={status.value}>
									{status.label}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Assigned Project Manager */}
				<div className="form-group">
					<label htmlFor="assigned_to" className="form-label">
						<User size={16} />
						Assigned Project Manager
					</label>
					<select
						id="assigned_to"
						name="assigned_to"
						className="form-select"
						value={data.assigned_to || ''}
						onChange={handleChange}
						disabled={disabled}
					>
						<option value="">No Project Manager Assigned</option>
						{teamMembers
							.filter((user) => user.role_slug === 'project-manager' || user.role_name === 'Project Manager')
							.map((pm) => (
								<option key={pm.id} value={pm.id}>
									{pm.first_name} {pm.last_name} ({pm.email})
								</option>
							))}
					</select>
					<small className="form-help">
						Required for submitting project for approval workflow
					</small>
				</div>
			</div>

			<div className="form-section">
				<h3 className="form-section__title">
					<Calendar size={20} />
					Timeline
				</h3>

				<div className="form-row form-row--2">
					<div className="form-group">
						<label htmlFor="start_date" className="form-label">
							Start Date
						</label>
						<input
							type="date"
							id="start_date"
							name="start_date"
							className="form-input"
							value={data.start_date || ''}
							onChange={handleChange}
							disabled={disabled}
						/>
					</div>

					<div className="form-group">
						<label htmlFor="target_date" className="form-label">
							Target Completion Date
						</label>
						<input
							type="date"
							id="target_date"
							name="target_date"
							className="form-input"
							value={data.target_date || ''}
							onChange={handleChange}
							disabled={disabled}
						/>
					</div>
				</div>
			</div>

			<div className="form-section">
				<h3 className="form-section__title">
					<Building2 size={20} />
					Business Context
				</h3>

				<div className="form-row form-row--2">
					<div className="form-group">
						<label htmlFor="industry" className="form-label">
							<Building2 size={16} />
							Industry
						</label>
						<select
							id="industry"
							name="industry"
							className="form-select"
							value={data.industry || ''}
							onChange={handleChange}
							disabled={disabled}
						>
							{industries.map((industry) => (
								<option key={industry.value} value={industry.value}>
									{industry.label}
								</option>
							))}
						</select>
					</div>

					<div className="form-group">
						<label htmlFor="domain" className="form-label">
							<Globe size={16} />
							Domain/Scope
						</label>
						<input
							type="text"
							id="domain"
							name="domain"
							className="form-input"
							value={data.domain || ''}
							onChange={handleChange}
							disabled={disabled}
							placeholder="e.g., Cloud Infrastructure, Data Privacy"
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

BasicDetailsForm.propTypes = {
	data: PropTypes.shape({
		name: PropTypes.string,
		description: PropTypes.string,
		type: PropTypes.string,
		priority: PropTypes.string,
		status: PropTypes.string,
		assigned_to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		start_date: PropTypes.string,
		target_date: PropTypes.string,
		industry: PropTypes.string,
		domain: PropTypes.string,
	}).isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	teamMembers: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			first_name: PropTypes.string,
			last_name: PropTypes.string,
			email: PropTypes.string,
			role_slug: PropTypes.string,
			role_name: PropTypes.string,
		})
	),
}

BasicDetailsForm.defaultProps = {
	disabled: false,
	teamMembers: [],
}

export default BasicDetailsForm
